<template>
  <h1>Driver Performance</h1>

  <div class="content-section filters">

    <div class="alert alert-warning" role="alert">After setting filters or changing sorting order, click "Apply filters"
      to re-query the database</div>
    <div class="row">
      <div class="col-md-4">
        <FormKit
            type="datepicker"
            label="ManifestDate From"
            v-model="selectedStartDate"
            format="DD/MM/YYYY"
            timezone="Europe/London"
            clearable
            :key="datepickerClearedKey"
        ></FormKit>
      </div>

      <div class="col-md-4">
        <FormKit
            type="datepicker"
            label="ManifestDate To"
            v-model="selectedEndDate"
            format="DD/MM/YYYY"
            timezone="Europe/London"
            clearable
            :key="datepickerClearedKey"
        ></FormKit>
      </div>
    </div>
    <div class="action-bar">
      <button @click="clearFilter" id="clearFilter">Clear filter</button>&#160;
      <button @click="exportZip" id="exportZip">Export Logs</button>&#160;
      <button @click="applyFilter" id="applyFilter1">Apply filter</button>
    </div>

    <div v-if="isListing">
      <easy-data-table
          v-model:server-options="serverOptions"
          :server-items-length="serverItemsLength"
          :headers="headers"
          :items="driverLogs"
          :search-field="searchField"
          :search-value="searchValue"
          :loading="loading"
          alternating
          border-cell
          buttons-pagination
          multi-sort
      >
        <template #header="header">
          <div class="customize-header">

            {{ header.text }}<br />

            <input v-if="header.value !== 'operations'" type="text" :id="'filter-' + header.value" @click.stop />
            <button v-else @click="applyFilter" id="applyFilter2">Apply filter</button>
          </div>
        </template>

        <template #item-manifestdate="item">
          {{formatDateEN(item.manifestdate)}}
        </template>

        <template #item-time_google="item">
          {{formatSeconds(item.time_google)}}
        </template>
        <template #item-time_estimate="item">
          {{formatSeconds(item.time_estimate)}}
        </template>

        <template #item-distance="item">
          {{formatDistance(item.distance)}} km
        </template>

      </easy-data-table>
    </div>
  </div>
</template>


<script setup>
import {ref, watch} from "vue";
import LogsService from "@/services/LogsService";

const driverLogs =  ref([]);
const searchField = ref("manifestdate");
const searchValue = ref("");

const selectedStartDate = ref('');
const selectedEndDate = ref('');
const datepickerClearedKey = ref(0);

const forceRenderDatepicker = () => {
  datepickerClearedKey.value += 1;
};
const headers = [
  { text: "First name", value: "firstname", sortable: true  },
  { text: "Surname", value: "surname", sortable: true  },
  { text: "ManifestDate", value: "manifestdate", sortable: true  },
  { text: "Round", value: "round_id", sortable: true  },
  { text: "Drive Time", value: "time_google", sortable: true  },
  { text: "Round Length", value: "time_estimate", sortable: true  },
  { text: "Distance", value: "distance", sortable: true  },
  { text: "Deliveries", value: "deliveries", sortable: true  },
  { text: "Collections", value: "collections", sortable: true  },
  { text: "Locations #", value: "location_count", sortable: true  },

  { text: "Actions", value: "operations" },
];

const isListing = ref(true);
const loading = ref(false);
const serverItemsLength = ref(0);
const serverOptions = ref({
  page: 1,
  rowsPerPage: 25,
  //sortBy: ["manifestdate"],
  sortType: ["desc"],
  exportFormat: "none",
});

const formatDateEN = (date) => {
  //25/12/2023
  const options = { year: 'numeric', month: 'numeric', day: 'numeric' }

  return new Date(date).toLocaleDateString('en-GB', options);
}
const formatSeconds = (seconds) => {
  var fm = [
    //Math.floor(s / 60 / 60 / 24), // DAYS
    Math.floor(seconds / 60 / 60) % 24, // HOURS
    Math.floor(seconds / 60) % 60, // MINUTES
    seconds % 60 // SECONDS
  ];
  return fm.map(function(v, i) { return ((v < 10) ? '0' : '') + v; }).join(':');
}
const formatDistance = (meters) => {

  return (meters/1000).toFixed(2);
}

const  createDateAsUTC = (date) => {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()));
}
const loadFromServer = async () => {
  loading.value = true;
  const {
    serverCurrentPageItems,
    serverTotalItemsLength,
  } = await LogsService.searchDriverPerformanceLogs(serverOptions.value);
  driverLogs.value = serverCurrentPageItems;
  serverItemsLength.value = serverTotalItemsLength;
  loading.value = false;
};

const applyFilter = (resetPage) => {
  let filters = {};
  let updateOptions = serverOptions.value;
  for (let i = 0; i < headers.length; i++) {
    const filterProperty = headers[i].value;
    if (filterProperty !== "operations") {
      filters[headers[i].value] = document.getElementById("filter-" + filterProperty).value;
    }
  }
  updateOptions.filters = filters;

  if (selectedStartDate.value !== "" && typeof selectedStartDate.value != "undefined") {
    selectedStartDate.value = createDateAsUTC(new Date(selectedStartDate.value));
    updateOptions.start_date = selectedStartDate.value;
  }
  if (selectedEndDate.value !== "" && typeof selectedEndDate.value != "undefined") {
    selectedEndDate.value = createDateAsUTC(new Date(selectedEndDate.value));
    updateOptions.end_date = selectedEndDate.value;
  }
  if (resetPage) {
    updateOptions.page = 1;
  }
  serverOptions.value = updateOptions;
  loadFromServer();
};

const exportZip = async () => {
  loading.value = true;
  await LogsService.downloadDriverPerformanceLogs(serverOptions.value);
  loading.value = false;
};

const clearFilter = () => {
  let filters = {};
  for (let i = 0; i < headers.length; i++) {
    const filterProperty = headers[i].value;
    if (filterProperty !== "operations") {
      filters[headers[i].value] = "";
      document.getElementById("filter-" + filterProperty).value = "";
    }
  }

  const updateServerOptions = serverOptions.value;
  updateServerOptions.filters = filters;
  updateServerOptions.page = 1;
  updateServerOptions.start_date = "";
  updateServerOptions.end_date = "";
  selectedStartDate.value = null;
  selectedEndDate.value = null;
  forceRenderDatepicker();
  serverOptions.value = updateServerOptions;

  loadFromServer();
};

// initial load
loadFromServer();

watch(() => serverOptions.value.page, () => {
  applyFilter(false);
});

</script>
